import { Component } from '@angular/core';
import { DecimalPipe, NgClass } from '@angular/common';
import { LucideAngularModule, Flame, Phone, Sparkles } from 'lucide-angular';
import { AI_SALES_ASSISTANT_BOARD, Lead, SignalClass } from './ai-sales-assistant.data';

/**
 * TVG-147: AI Sales Assistant (Sales AI POC).
 *
 * A static, staff-only proof-of-concept page. The ten couples and all chrome
 * values are hard coded in `ai-sales-assistant.data.ts` — no backend, no API,
 * no calculation. The list ranks couples by who to call first; selecting a row
 * opens the detail panel built from that lead's fields.
 *
 * The route is gated by the `sales-ai` feature toggle (FeatureToggleGuard),
 * so the page only renders where Sales AI is enabled.
 */
@Component({
  selector: 'app-ai-sales-assistant',
  standalone: true,
  templateUrl: './ai-sales-assistant.component.html',
  styleUrl: './ai-sales-assistant.component.css',
  imports: [NgClass, DecimalPipe, LucideAngularModule],
})
export class AiSalesAssistantComponent {
  protected readonly board = AI_SALES_ASSISTANT_BOARD;

  protected readonly Flame = Flame;
  protected readonly Phone = Phone;
  protected readonly Sparkles = Sparkles;

  /** Active venue filter chip; 'All venues' shows every lead. */
  protected selectedVenue = 'All venues';

  /** The lead whose detail panel is open. Defaults to the top-ranked couple. */
  protected selectedLead: Lead = AI_SALES_ASSISTANT_BOARD.leads[0];

  protected get visibleLeads(): Lead[] {
    if (this.selectedVenue === 'All venues') {
      return this.board.leads;
    }
    return this.board.leads.filter((lead) => lead.venue === this.selectedVenue);
  }

  protected selectVenue(venue: string): void {
    this.selectedVenue = venue;
    // Keep the open detail panel in sync with the filtered list.
    if (venue !== 'All venues' && this.selectedLead.venue !== venue) {
      const first = this.visibleLeads[0];
      if (first) {
        this.selectedLead = first;
      }
    }
  }

  protected isVenueSelected(venue: string): boolean {
    return this.selectedVenue === venue;
  }

  protected selectLead(lead: Lead): void {
    this.selectedLead = lead;
  }

  protected isLeadSelected(lead: Lead): boolean {
    return this.selectedLead.id === lead.id;
  }

  /**
   * Tailwind classes for a signal tag, keyed by the colour class (the first
   * item of each signal pair). 'value' is the gold high-value treatment.
   */
  protected signalClasses(colour: SignalClass): string {
    switch (colour) {
      case 'value':
        return 'bg-amber-50 text-amber-700 ring-1 ring-amber-300 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/40';
      case 'amber':
        return 'bg-orange-50 text-orange-700 ring-1 ring-orange-200 dark:bg-orange-500/15 dark:text-orange-300 dark:ring-orange-500/30';
      case 'blue':
        return 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30';
      case 'green':
        return 'bg-green-50 text-green-700 ring-1 ring-green-200 dark:bg-green-500/15 dark:text-green-300 dark:ring-green-500/30';
      case 'red':
        return 'bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-500/15 dark:text-red-300 dark:ring-red-500/30';
      case 'grey':
      default:
        return 'bg-gray-100 text-gray-600 ring-1 ring-gray-200 dark:bg-white/5 dark:text-white/70 dark:ring-white/10';
    }
  }

  /** Band pill colour: Hot is red/orange, Warm is amber. */
  protected bandClasses(band: Lead['band']): string {
    return band === 'hot'
      ? 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300'
      : 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
  }
}
